import { Box, Link } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet-async';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import SigninBg from 'src/assets/tailwind/signinBg.svg';
import KCThemeTemplate from 'src/components/KCThemeTemplate';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { BASE_SERVER_URL, ENV_BASE_URL, IS_SELF_SIGNUP_ENABLED } from 'src/config';
import useAnalytics from 'src/hooks/useAnalytics';
import {
  getClientId,
  getKcLang,
  getRealmName,
  getUserEmailAddress,
  kcinit,
  setUserEmailAddress
} from 'src/keycloak';
import { setAlert } from 'src/redux/slices/snackbar';
import { useDispatch } from 'src/redux/store';
import { StringKeys } from 'src/types/base';
import { ALERT } from 'src/types/enum';
import {
  SIGNUP_CONTINUE_CLICKED,
  SIGNUP_PAGE_VIEWED,
  SIGNUP_VERIFICATION_EMAIL_SENT
} from 'src/utils/analyticsUtils';
import {
  DEFAULT_APPLICATION_ROUTE,
  SERVER_DOWN_CODES
} from 'src/utils/constants';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { isEmailValid, SUPPORT_EMAIL_EGPT, fetchTenantInfo, applyTenantInfo, validateEmailField, sanitizeEmailInput } from 'src/utils/utils';

import DomainNotFoundError from './DomainNotFoundError';
import SelfSignupConfirmation from './SelfSignupConfirmation';
import SelfSignupHeader from './SelfSignupHeader';
import { StyledLink } from './styles';
import { Typography } from 'src/components/ui/typography';

interface IFormInput {
  email: string;
}

const TenantDiscovery = () => {
  const { t } = useTranslation();
  const collectAnalytics = useAnalytics();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState<boolean>(false);
  const [isInvitationSent, setIsInvitationSent] = useState<boolean>(false);
  const [renderErrors, setRenderErrors] = useState<boolean>(false);
  const [showDomainNotFoundError, setShowDomainNotFoundError] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const setEmailErrorMessage = (type: string, msg: string) => {
    setError('email', {
      type: type,
      message: msg
    });
  };

  const handleEmailChange = (onChange: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('email');
    onChange(sanitizeEmailInput(e.target.value));
  };

  const handleBackBtn = () => {
    setIsInvitationSent(false);
    reset();
  };

  useEffect(() => {
    if (window.location.origin !== ENV_BASE_URL) {
      verifyDomain();
    }
  }, []);

  const verifyDomain = () => {
    setIsVerifyingDomain(true);
    fetchTenantInfo().then((info) => {
      if (!info?.realm || !info?.clientId) {
        logger.warn(t(T.errorDiscoveringTenant));
        setShowDomainNotFoundError(true);
        setIsVerifyingDomain(false);
        return;
      }
      applyTenantInfo(info);
      kcinit({ realm: info.realm, clientId: info.clientId, userEmail: null })
        .then(() => {
          navigate(DEFAULT_APPLICATION_ROUTE);
        })
        .catch((error) => {
          logger.log(error);
          setIsVerifyingDomain(false);
        });
    });
  };

  const navigateToKcRealm = (response?: StringKeys, userEmail?: string) => {
    let realm = getRealmName();
    let clientId = getClientId();
    let userEmailAddress = getUserEmailAddress();

    if (response && userEmail) {
      realm = response?.data?.ret_val?.data?.realm_name;
      clientId = response?.data?.ret_val?.data?.client_id;
      userEmailAddress = userEmail;
    }

    kcinit({
      realm: realm,
      clientId: clientId,
      userEmail: isEmailValid(userEmailAddress) ? userEmailAddress : null
    })
      .then(() => {
        navigate(DEFAULT_APPLICATION_ROUTE);
      })
      .catch((error) => {
        logger.log(error);
      });
  };

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
    setError,
    control,
    clearErrors,
    trigger,
    watch
  } = useForm<IFormInput>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const emailValue = watch('email');
  const isEmailEmpty = !emailValue;
  const buttonText = isEmailEmpty ? t(T.enterEmail) : t(T.continue);

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);
    const savedLang = getKcLang();
    const userEmail = data?.email?.toLowerCase() || null;

    if (!userEmail) {
      setEmailErrorMessage('manual', t(T.invalidEmail));
      return false;
    }

    fetchTenantInfo({ email: userEmail, language: savedLang })
      .then((info) => {
        if (!info?.realm || !info?.clientId) {
          setEmailErrorMessage('manual', t(T.invalidDomain));
          setIsLoading(false);
          return;
        }
        setUserEmailAddress(userEmail);
        applyTenantInfo(info);
        navigateToKcRealm(undefined, userEmail);
        setIsLoading(false);
      })
      .catch((error) => {
        logger.log(error?.response);
        setIsLoading(false);
        if (SERVER_DOWN_CODES.includes(error?.response?.status)) {
          dispatch(
            setAlert({
              msg: t(T.serverNotReachable),
              type: ALERT.ERROR
            })
          );
        } else {
          setEmailErrorMessage('manual', t(T.invalidDomain));
        }
      });
  };

  useEffect(() => {
    if (!renderErrors || !isSubmitted) {
      return;
    }

    clearErrors();
    trigger();

    setRenderErrors(false);
  }, [renderErrors, isSubmitted, clearErrors, trigger]);

  const loginSilently = async (username, password) => {
    const body = new URLSearchParams({
      grant_type: 'password',
      client_id: 'expertadvisor',
      username,
      password,
      scope: 'openid profile email'
    });

    const res = await fetch(
      'https://localhost/auth/realms/expertadvisor/protocol/openid-connect/token',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
      }
    );
    if (!res.ok) throw new Error('Login failed');
    const access_token = res.json(); // access_token
    return access_token;
  };

  return (
    <div
      className="tw:min-h-screen  tw:h-screen tw:bg-cover tw:bg-center tw:bg-no-repeat tw:font-['Lato'] tw:relative tw:flex tw:flex-col tw:items-center tw:justify-center tw:sm:py-8 tw:px-6 tw:w-full tw:overflow-y-auto "
      style={{ backgroundImage: `url(${SigninBg})` }}
    >
      <Helmet title={`${t(T.empowerGPT)} - ${t(T.signIn)}`} />
      {!isVerifyingDomain && (
        <KCThemeTemplate
          header={
            showDomainNotFoundError ? (
              <DomainNotFoundError />
            ) : isInvitationSent ? (
              <SelfSignupConfirmation handleBackBtn={handleBackBtn} />
            ) : null
          }
          showFooter={false}
          setRenderErrors={setRenderErrors}
        >
          {!isInvitationSent && !showDomainNotFoundError && (
            <div className="tw:flex tw:flex-col tw:items-center tw:justify-start tw:w-full tw:gap-6 ">
              {/* Header Section - Outside the card */}
              <Box textAlign="center" mb={2}>
                <SelfSignupHeader isLogin />
              </Box>

              {/* Form Card */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="tw:w-full tw:flex tw:flex-col tw:items-center "
              >
                <Box className="tw:w-full tw:gap-6 tw:flex tw:flex-col">
                  {/* OAuth Buttons */}
                  {/* <div className="tw:space-y-3">
                    <SocialLoginButton
                      icon={GoogleLogo}
                      text={t(T.continueWithGoogle)}
                    />
                    <SocialLoginButton
                      icon={MicrosoftLogo}
                      text={t(T.continueWithMicrosoft)}
                    />
                  </div>
                  <OrDivider /> */}
                  <Box className="tw:flex tw:flex-col tw:gap-2">
                    <Typography className="tw:text-text-secondary tw:text-[14px] tw:font-medium tw:leading-4 tw:tracking-[0]">
                      {t(T.emailAddress)}
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: t(T.emailRequired) as string,
                        validate: (value) => {
                          const error = validateEmailField(value?.toLowerCase().trim());
                          return error ? (t(error as any) as string) : true;
                        }
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Input
                          height="42px"
                          placeholder="JohnDoe@example.com"
                          error={!!errors.email}
                          helperText={errors.email ? errors.email.message : ''}
                          disabled={isLoading}
                          autoFocus
                          onChange={handleEmailChange(onChange)}
                          value={value}
                          className="tw:text-text-primary tw:font-['Lato'] tw:text-lg tw:font-normal tw:leading-lg tw:tracking-[0]"
                        />
                      )}
                    />
                  </Box>
                  <Button
                    className={`tw:w-full tw:rounded-l tw:gap-2 tw:py-2.5 tw:font-medium tw:hover:bg-blue-700 tw:transition tw:hover:cursor-pointer 
                ${
                  isEmailEmpty
                    ? 'tw:bg-secondary tw:text-text-disabled'
                    : 'tw:bg-brand tw:text-text-over-brand'
                }
              `}
                    disabled={isEmailEmpty}
                  >
                    {buttonText}
                  </Button>

                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={{ xs: 2, sm: 3 }}
                    onClick={() => collectAnalytics(SIGNUP_PAGE_VIEWED)}
                  >
                    {IS_SELF_SIGNUP_ENABLED && (
                      <Typography className="tw:text-[14px] sm:tw:text-[14px] tw:font-normal tw:text-center tw:text-[#626262]">
                        {t(T.noAccount)}
                        <Link
                          href={`${BASE_SERVER_URL}/signup`}
                          underline="hover"
                          sx={StyledLink}
                        >
                          {`${t(T.signup)}`}
                        </Link>
                      </Typography>
                    )}

                    <Typography className="tw:text-[14px] sm:tw:text-[14px] tw:font-normal tw:text-center tw:text-[#626262]">
                      {t(T.needHelpContactUs)}
                      <Link
                        href={`mailto:${SUPPORT_EMAIL_EGPT}`}
                        underline="hover"
                        sx={StyledLink}
                      >
                        {SUPPORT_EMAIL_EGPT}
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </form>
            </div>
          )}
        </KCThemeTemplate>
      )}
    </div>
  );
};

export default TenantDiscovery;
